import Column from 'components/Column'
import AlertTriangleFilled from 'components/Icons/AlertTriangleFilled'
import { LoaderV2 } from 'components/Icons/LoadingSpinner'
import { TransactionStatus } from 'graphql/data/__generated__/types-and-hooks'
import useENSName from 'hooks/useENSName'
import { XCircle, Zap } from 'react-feather'
import styled from 'styled-components/macro'
import { EllipsisStyle, ThemedText } from 'theme'
import { ExplorerDataType, getExplorerLink } from 'utils/getExplorerLink'

import { PortfolioLogo } from '../PortfolioLogo'
import PortfolioRow from '../PortfolioRow'
import { useTimeSince } from './parseRemote'
import { Activity } from './types'

const ActivityRowDescriptor = styled(ThemedText.BodySmall)`
  color: ${({ theme }) => theme.textSecondary};
  ${EllipsisStyle}
`

const StyledTimestamp = styled(ThemedText.Caption)`
  color: ${({ theme }) => theme.textSecondary};
  font-variant: small;
  font-feature-settings: 'tnum' on, 'lnum' on, 'ss02' on;
`

const SpeedUpBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 12px;
  line-height: 1;
  padding: 6px 8px;
  width: max-content;
  border-radius: 999px;
  border: 0;
  cursor: pointer;
  color: #fff;
  background: linear-gradient(90deg, #9139b0 0%, #4261d6 100%);
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.06) inset;

  &:hover {
    filter: brightness(1.03);
  }
  &:active {
    transform: translateY(0.5px);
  }
`

const RightStack = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
`

const Badge = styled.span<{ $type: 'speedup' | 'cancelled' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${({ $type }) => ($type === 'speedup' ? 'rgba(45, 96, 255, 0.15)' : 'rgba(255, 67, 87, 0.15)')};
  color: ${({ $type }) => ($type === 'speedup' ? '#5D9CFF' : '#FF6B81')};
  border: 1px solid ${({ $type }) => ($type === 'speedup' ? 'rgba(45, 96, 255, 0.3)' : 'rgba(255, 67, 87, 0.3)')};
`

export function ActivityRow({
  activity,
  isSpeedUpCandidate,
  onSpeedUp,
}: {
  activity: Activity
  isSpeedUpCandidate?: boolean
  onSpeedUp?: () => void
}) {
  const {
    chainId,
    status,
    title,
    descriptor,
    logos,
    otherAccount,
    currencies,
    timestamp,
    hash,
    isSpedUp,
    isCancelled,
  } = activity
  const { ENSName } = useENSName(otherAccount)
  const timeSince = useTimeSince(Number(timestamp) || 0)
  const explorerUrl = +chainId && hash ? getExplorerLink(chainId, hash, ExplorerDataType.TRANSACTION) : undefined

  return (
    <PortfolioRow
      left={
        <Column>
          <PortfolioLogo chainId={chainId} currencies={currencies} images={logos} accountAddress={otherAccount} />
        </Column>
      }
      title={<ThemedText.SubHeader>{title}</ThemedText.SubHeader>}
      descriptor={
        <ActivityRowDescriptor color="textSecondary">
          {descriptor}
          {ENSName ?? otherAccount}
        </ActivityRowDescriptor>
      }
      right={
        status === TransactionStatus.Pending ? (
          <RightStack>
            <LoaderV2 />
            {isSpeedUpCandidate && (
              <SpeedUpBtn
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onSpeedUp?.()
                }}
                title="Speed up"
              >
                Speed up
              </SpeedUpBtn>
            )}
          </RightStack>
        ) : status === TransactionStatus.Confirmed ? (
          <RightStack>
            {isSpedUp && (
              <Badge $type="speedup" title="Transaction was sped up">
                <Zap size={14} />
              </Badge>
            )}
            {isCancelled && (
              <Badge $type="cancelled" title="Transaction was cancelled">
                <XCircle size={14} />
              </Badge>
            )}
            <StyledTimestamp>{timeSince || '-'}</StyledTimestamp>
          </RightStack>
        ) : (
          <AlertTriangleFilled />
        )
      }
      onClick={() => {
        if (explorerUrl) window.open(explorerUrl, '_blank')
      }}
    />
  )
}
